import { Component, Input, Output, OnInit, Inject, EventEmitter } from '@angular/core';
import { ScimMappingConfigService } from '@netfoundry-ui/shared/apiv2';
import { ScimMappingConfig, ValueMapping, DirectValueFactory } from '@netfoundry-ui/shared/model';
import { GrowlerService, ZitiDataService, ZITI_DATA_SERVICE } from 'ziti-console-lib';
import {isEmpty} from 'lodash';
import { GrowlerData } from "@netfoundry-ui/shared/growler";
import { Subject } from 'rxjs';

@Component({
    selector: 'lib-update-scim-mapping-config',
    templateUrl: './update-scim-mapping-config.component.html',
    styleUrls: ['./update-scim-mapping-config.component.scss'],
    standalone: false
})
export class UpdateScimMappingConfigComponent implements OnInit {
    
  @Output() wasUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input() scimMappingConfigModel = new ScimMappingConfig;
  @Input() integrationId: string;
  @Input() showUpdateMappingConfig = true;
  @Input() showApply = false;
  @Input() showHeader = true;
  @Input() save: Subject<any>;
  
  NONE = "none";
  OTHER = "other";
  USER_NAME = "userName";
  PRIMARY_EMAIL = "primaryEmail";
   
  primaryEmailString = "$.emails[?(@.primary == true)].value";
  externalIdSelection = this.NONE;

  valueMappings: Map<string, any>;
  authPolicies: any = [];
  customExternalIdMapping: any;

  scimMappingConfigService: ScimMappingConfigService;
  directFields: any = ["userName", "displayName", "externalId", "formattedName", "nickName"];
  identityNameMappings: any = [{display: "UserName", value: "userName"}, {display: "Display Name", value: "displayName"}, {display: "Formatted Name", value: "formattedName"}, {display: "Nickname", value: "nickName"}]; 
  isLoading = false;
  processingMappingConfig = false;

  mappingConfigErrMap:  any = {};

  constructor(
    public mappingConfigService: ScimMappingConfigService,
    private growlerService: GrowlerService,
    @Inject(ZITI_DATA_SERVICE) protected zitiService: ZitiDataService,
  ) {
    
    this.scimMappingConfigService = mappingConfigService;
    this.getAuthPolicies();
    this.valueMappings = new Map<string, ValueMapping>();
    this.customExternalIdMapping = new ValueMapping("externalId", new DirectValueFactory(''));
  }

  ngOnInit(): void {
        
    this.isLoading = true;
    this.getMappingConfigPromise().finally(() => {
        this.isLoading = false;
    });

    this.save.subscribe(() => {
        this.updateMappingConfig();
    })
  }

        

  getAuthPolicies() {
    const paging = {
      filter: "",
      noSearch: true,
      order: "asc",
      page: 1,
      searchOn: "name",
      sort: "name",
      total: 100
    }
    this.zitiService.get('auth-policies', paging, []).then((result: any) => {
      this.authPolicies = [...result.data];
    });
  }


  getMappingConfigPromise() {
    return this.scimMappingConfigService.getMappingConfigForIntegration(this.integrationId).then((mappingConfig: ScimMappingConfig | undefined) => {
      if(mappingConfig !== undefined) {
          this.scimMappingConfigModel = mappingConfig;
          mappingConfig.mappings.forEach((mapping: any) => {
              this.valueMappings.set(mapping?.targetProperty, mapping);
              if(mapping?.targetProperty === "externalId") {

                  if(mapping?.valueFactory?.propertyName === this.USER_NAME) {
                    this.externalIdSelection = this.USER_NAME;
                  } else if (mapping?.valueFactory?.propertyName === this.primaryEmailString) {
                    this.externalIdSelection = this.PRIMARY_EMAIL;
                  } else {
                    this.customExternalIdMapping = mapping;
                    this.externalIdSelection = this.OTHER;
                  }
              }
          });
          if (this.externalIdSelection === undefined) {
            this.externalIdSelection = this.OTHER;
          }
          return mappingConfig;
      };
    });
  }

  validateMappingConfig() {
    this.mappingConfigErrMap = {};

    if(!this.valueMappings.get("name") || isEmpty(this.valueMappings.get("name")?.valueFactory?.propertyName)) {
      this.mappingConfigErrMap['name'] = true;
    }

    if(!this.valueMappings.get("authPolicyId") || isEmpty(this.valueMappings.get("authPolicyId")?.valueFactory?.staticValue)) {
      this.mappingConfigErrMap['authPolicyId'] = true;
    }
    
    return isEmpty(this.mappingConfigErrMap);
  }

  applyMappingConfig() {

    if(this.scimMappingConfigModel && !this.validateMappingConfig()) {
        return;
    }
    
    const updateMappingConfigProm = this.updateMappingConfig();
    
    updateMappingConfigProm.then((result) => {
      if(result === true) {
        this.growlerService.show(new GrowlerData(
            'success',
            'Success',
            'Successfully Updated Mapping Config',
            'The SCIM Mapping Config has been successfully updated'
        ))
      }
    }).finally(() => {
      
    });
  }

  updateMappingConfig() {

    // workaround to get IDE to stop complaining about externalIdMapping's ValueFactory not containing a field 'propertyName'
    // ValueFactory is a generic for two types, static and direct. In this context, externalId mappings are always direct and as such have a propertyName field
    let externalIdMapping: any;
    externalIdMapping = new ValueMapping("externalId", new DirectValueFactory(''));

    // if the externalIdSelection was userName, set the direct value factory to the known userName property
    // if it was primary email, set it to the known JsonPath expression for primary email
    // otherwise, leave it as is
    switch(this.externalIdSelection) {
      case this.USER_NAME:
        externalIdMapping.valueFactory = new DirectValueFactory(this.USER_NAME);
        break;
      case this.PRIMARY_EMAIL:
        externalIdMapping.valueFactory = new DirectValueFactory(this.primaryEmailString);
        break;
      case this.OTHER:
        externalIdMapping = this.customExternalIdMapping;
        break;
      default:
        // if not a defined case (i.e. None is selected) set the external ID mapping to a direct value factory with an empty string, so any existing externalId mapping is deleted
        externalIdMapping.valueFactory = new DirectValueFactory("");
        break;
    }

    // externalId was previously set but is now removed
    if((externalIdMapping.valueFactory.propertyName === undefined || isEmpty(externalIdMapping.valueFactory.propertyName.trim())) && this.valueMappings.has("externalId")) {
      this.valueMappings.delete("externalId");
    } else if(!isEmpty(externalIdMapping.valueFactory.propertyName)) {

      externalIdMapping.valueFactory.propertyName = externalIdMapping.valueFactory.propertyName.trim();
      this.valueMappings.set("externalId", externalIdMapping);
    }

    this.scimMappingConfigModel.mappings = Array.from(this.valueMappings.values());

    this.processingMappingConfig = true;
    const mappingConfigUpdatePromise = this.scimMappingConfigService.save(this.scimMappingConfigModel).then((result) => {
      this.processingMappingConfig = false;
      if(result.length > 0) {
        this.mappingConfigErrMap = this.processErrors(result);
        this.wasUpdated.emit(false);
        return false;
      }
      this.wasUpdated.emit(true);
      return true;
    });

    return mappingConfigUpdatePromise;
  }

  private processErrors(errors: any) {

    // handle system error
    if(errors[0] ['http'] || errors[0] ['invalid']) {
      this.growlerService.show(
          new GrowlerData(
          'error',
          'SCIM Integration update request failed. ',
          errors[0] ['http']?.message ?? errors[0] ['invalid']
          )
      );
      return {};
    } else {
      const errorMap: any = {};
      errors.forEach((err: any) => {
          errorMap[err.label] = err.message;
      })

      return errorMap;

    }
  }
  

}